import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * "Install Northline" prompt. Listens for the browser's `beforeinstallprompt`
 * event (Chrome/Edge/Samsung Internet on Android) and offers the user a
 * button to install the PWA to their home screen.
 * - Silently hides when the app is already installed / running standalone.
 * - Dismissible; remembers dismissal in localStorage for a week.
 */
const DISMISS_KEY = "crm_pwa_install_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

export function InstallPWAButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Detect standalone / already-installed state.
    const mm = typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)");
    if ((mm && mm.matches) || window.navigator.standalone === true) {
      setInstalled(true);
    }
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (at && Date.now() - at < DISMISS_MS) setDismissed(true);

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed || !deferred) return null;

  const install = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") setInstalled(true);
    } catch (_) {}
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className={`glass mb-4 flex items-center gap-3 rounded-lg p-3 animate-fade-up ${className}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
        <Download className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm font-bold leading-tight">Install Northline</div>
        <div className="text-xs text-muted-foreground">Add to your home screen for instant lead alerts.</div>
      </div>
      <button
        onClick={install}
        data-testid="pwa-install-btn"
        className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground tap-lift"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
